import { DEFAULT_MAP_BASEMAP } from './mutations.js'

// A layer is only ever rendered through a map, so one that no map claims would be
// invisible. These layers are collected into a frontend-only map instead — it has no
// row in the database and is never sent to the API.
export const VIRTUAL_MAP_ID = '__unassigned__'
const VIRTUAL_MAP_NAME = 'Unassigned'
const VIRTUAL_MAP_COLOR = '#64748b'

export const getters = {
  getMaps: state => (orgId) => {
    return state.maps[orgId] || []
  },

  // Top-level org layers that no persisted map claims — the safety net for any
  // backend path that fails to write a map_layer row.
  getUnassignedLayerCodes: (state, _getters, _rootState, rootGetters) => (orgId) => {
    // Layers are fetched before maps, so an absent entry means "not loaded yet" —
    // treating that as "nothing claims them" would flash every layer as unassigned.
    const maps = state.maps[orgId]
    if (!maps) return []
    const claimed = new Set(maps.flatMap(m => m.layerCodes))
    return (rootGetters['layers/getOrgLayers'](orgId) || [])
      .filter(l => l.properties?.type !== 0 && !claimed.has(l.uuid))
      .map(l => l.uuid)
  },

  getVirtualMap: (state, getters) => (orgId) => {
    const layerCodes = getters.getUnassignedLayerCodes(orgId)
    if (!layerCodes.length) return null
    return {
      id: VIRTUAL_MAP_ID,
      name: VIRTUAL_MAP_NAME,
      isVirtual: true,
      isDefault: false,
      color: VIRTUAL_MAP_COLOR,
      icon: 'Map',
      basemap: getters.getDefaultMap(orgId)?.basemap || DEFAULT_MAP_BASEMAP,
      shared: false,
      visible: true,
      layerCodes
    }
  },

  // Everything the UI can render. `getMaps` stays the persisted-only list: URL state
  // and the move-to-map menus must never offer the virtual map as a target.
  getAllMaps: (state, getters) => (orgId) => {
    const virtual = getters.getVirtualMap(orgId)
    const maps = getters.getMaps(orgId)
    return virtual ? [...maps, virtual] : maps
  },

  getMapById: (state, getters) => (orgId, mapId) => {
    return getters.getAllMaps(orgId).find(m => m.id === mapId)
  },

  getDefaultMap: state => (orgId) => {
    return (state.maps[orgId] || []).find(m => m.isDefault)
  },

  // Resolve a map's layerCodes to live layer objects from the layers module,
  // preserving the map's own ordering. Layers not yet fetched are skipped.
  getMapLayers: (state, getters, _rootState, rootGetters) => (orgId, mapId) => {
    const map = getters.getAllMaps(orgId).find(m => m.id === mapId)
    if (!map) return []
    const orgLayers = rootGetters['layers/getOrgLayers'](orgId) || []
    const byCode = new Map(orgLayers.map(l => [l.uuid, l]))
    // PlanetScope (type 0) is now a basemap, not a layer — never list it as a row.
    return map.layerCodes
      .map(code => byCode.get(code))
      .filter(Boolean)
      .filter(l => l.properties?.type !== 0)
  },

  getMapForLayer: (state, getters) => (orgId, layerCode) => {
    return getters.getAllMaps(orgId).find(m => m.layerCodes.includes(layerCode))
  },

  getMapMembers: state => (mapId) => {
    return state.members[mapId] || []
  },

  getMapInvites: state => (mapId) => {
    return state.invites[mapId] || []
  }
}
